import type { SelectOption } from 'naive-ui'
import type { AddressAutoComplete } from '~/types'
import { refDebounced } from '@vueuse/core'
import PinIcon from '~/assets/icons/places/pin.svg'

export default (query: MaybeRef<string> = '') => {
  const { public: { addressApiUrl } } = useRuntimeConfig()

  const isEnabled = computed(() => Boolean(addressApiUrl))
  const searchtext = toRef(query)
  const debouncedSearch = refDebounced(searchtext, 750, { maxWait: 1000 })

  const { addressQuery } = useQuery()
  const { data: suggestionsData } = addressQuery.autoCompleteFromQuery(debouncedSearch)
  const suggestions = computed(() => {
    if (!suggestionsData.value) {
      return []
    }
    return suggestionsData.value
  })

  // Logic taken from https://github.com/samhess/vue3-address-input/blob/main/src/AddressInput.vue#L37 to ensure that labels are properly formatted to reflect the search.
  function formatLabel(label: string, part: 'start' | 'middle' | 'end') {
    const index = label.toLowerCase().indexOf(searchtext.value.toLowerCase())
    if (index >= 0) {
      let text = ''
      switch (part) {
        case 'start':
          text = label.substring(0, index)
          break
        case 'middle':
          text = label.substring(index, index + searchtext.value.length)
          break
        case 'end':
          text = label.substring(index + searchtext.value.length)
          break
      }
      return text
    } else if (part === 'start') {
      return label
    } else {
      return ''
    }
  }

  function formatOptionLabel(option: SelectOption) {
    return h('div', { class: 'flex items-center gap-1.5' }, [
      h(PinIcon, { style: 'height: 20px!important;width: 20px!important' }),
      h('div', [
        h('span', formatLabel(option.label as string, 'start')),
        h('span', { class: 'text-primary-500' }, formatLabel(option.label as string, 'middle')),
        h('span', formatLabel(option.label as string, 'end')),
      ]),
    ])
  }

  function getAddressByLabel(label: string): AddressAutoComplete | undefined {
    const suggestionIDX = suggestions.value.findIndex(s => s.label === label)
    if (suggestionIDX === -1) {
      return
    }
    return suggestions.value[suggestionIDX]
  }

  function getAddressById(id: string): AddressAutoComplete | undefined {
    const suggestionIDX = suggestions.value.findIndex(s => s.id === id)
    if (suggestionIDX === -1) {
      return
    }
    return suggestions.value[suggestionIDX]
  }

  return {
    isEnabled,
    searchtext,
    suggestions,
    formatOptionLabel,
    getAddressById,
    getAddressByLabel,
  }
}
